import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Pencil, PencilRuler, X, Loader2, Upload, Eye, History, RotateCcw, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { api, fileUrl } from '../lib/api';
import { refreshPageContent } from '../lib/pageContent';
import { RichTextEditor } from './RichTextEditor';

/* True on-page editing (Phase 96, admin only).
   - A floating EDIT PAGE control appears ONLY for logged-in staff/admin users
     (validated against /api/auth/me), and only on desktop/tablet widths.
   - Turning on EDIT MODE outlines the editable areas and shows small
     EDIT HERO / EDIT THIS SECTION buttons beside them.
   - Every editor saves through the SAME Admin -> Page Content records
     (single source of truth), with the same server-side sanitization,
     so nothing here can break the approved design system.
   - Saves are explicit (SAVE / CANCEL, no autosave) with an
     unsaved-changes warning, hero PREVIEW before save, and a short
     Previous Versions history with one-click restore. */

const EditModeContext = createContext({
  isAdmin: false,
  editMode: false,
  setEditMode: () => {},
  drafts: {},
  setDraft: () => {},
  clearDraft: () => {},
});

export const useEditMode = () => useContext(EditModeContext);

export const useEditDraft = (key) => {
  const { editMode, drafts } = useEditMode();
  return editMode && key ? drafts[key] || null : null;
};

export const EditModeProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    let alive = true;
    let token = null;
    try { token = localStorage.getItem('w8_token'); } catch { token = null; }
    if (!token) return undefined;
    api.get('/auth/me')
      .then((r) => { if (alive && r.data && ['admin', 'staff'].includes(r.data.role)) setIsAdmin(true); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const setDraft = useCallback((key, value) => {
    setDrafts((d) => ({ ...d, [key]: value }));
  }, []);
  const clearDraft = useCallback((key) => {
    setDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  }, []);

  const toggle = useCallback((on) => {
    setEditMode(on);
    if (!on) setDrafts({});
  }, []);

  return (
    <EditModeContext.Provider value={{ isAdmin, editMode, setEditMode: toggle, drafts, setDraft, clearDraft }}>
      {children}
    </EditModeContext.Provider>
  );
};

/* Floating admin control: EDIT PAGE -> EDIT MODE ON -> EXIT EDIT MODE.
   Desktop/tablet only; regular visitors never see it. */
export const EditModeControl = () => {
  const { isAdmin, editMode, setEditMode } = useEditMode();
  if (!isAdmin) return null;
  return (
    <div className="fixed bottom-5 left-5 z-[240] hidden md:flex flex-col items-start gap-2" data-testid="edit-mode-control">
      {editMode && (
        <p className="rounded-lg bg-[#0b0f17] text-white text-xs font-bold px-3 py-2 shadow-lg max-w-[240px]" data-testid="edit-mode-hint">
          Edit Mode is ON. Click an Edit button beside a hero or section to change it. Nothing saves until you click Save.
        </p>
      )}
      <button
        type="button"
        onClick={() => setEditMode(!editMode)}
        className={`inline-flex items-center gap-2 h-11 px-4 rounded-full font-black text-sm tracking-wide shadow-xl btn-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${editMode
          ? 'bg-[#b91c1c] hover:bg-[#991b1b] text-white focus-visible:ring-[#b91c1c]'
          : 'bg-[#1a2744] hover:bg-[#24406f] text-white focus-visible:ring-[#1a2744]'}`}
        data-testid="edit-mode-toggle"
      >
        {editMode ? (<><X className="h-4 w-4" /> EXIT EDIT MODE</>) : (<><PencilRuler className="h-4 w-4" /> EDIT PAGE</>)}
      </button>
    </div>
  );
};

/* Small pill button shown beside an editable area while Edit Mode is ON */
export const EditBadge = ({ label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 rounded-full bg-[#1a2744] hover:bg-[#b91c1c] text-white text-[11px] font-black uppercase tracking-wide px-3 py-1.5 shadow-md btn-press focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a2744]"
    data-testid={testId}
  >
    <Pencil className="h-3 w-3" /> {label}
  </button>
);

// ---------------------------------------------------------------------------
// Shared editor plumbing
// ---------------------------------------------------------------------------
const splitKey = (fullKey) => {
  const i = (fullKey || '').indexOf('.');
  return i === -1 ? [fullKey, ''] : [fullKey.slice(0, i), fullKey.slice(i + 1)];
};

const useUnsavedWarning = (dirty) => {
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Leave without saving?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
};

const confirmDiscard = () => window.confirm('You have unsaved changes. Leave without saving?');

const saveSection = async (groupKey, sectionKey, body) => {
  const r = await api.put(`/admin/page-content/${groupKey}/section/${encodeURIComponent(sectionKey)}`, body);
  await refreshPageContent();
  return r.data;
};

const fmtRevTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

/* Previous Versions: date / time / admin user / one-click restore */
const RevisionsPanel = ({ groupKey, onRestored }) => {
  const [open, setOpen] = useState(false);
  const [revs, setRevs] = useState(null);
  const [busy, setBusy] = useState('');

  const load = () => api.get(`/admin/page-content/${groupKey}/revisions`).then((r) => setRevs(r.data || [])).catch(() => setRevs([]));

  const restore = async (rev) => {
    if (!window.confirm('Restore this previous version? The current wording will also be kept in Previous Versions.')) return;
    setBusy(rev.id);
    try {
      await api.post(`/admin/page-content/${groupKey}/revisions/${rev.id}/restore`);
      await refreshPageContent();
      toast.success('Previous version restored');
      onRestored?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setBusy(''); }
  };

  return (
    <div className="border-t border-slate-200 pt-3 mt-1" data-testid="edit-revisions-panel">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!revs) load(); }}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1a2744] underline underline-offset-4 decoration-slate-300 hover:decoration-[#b91c1c]"
        data-testid="edit-revisions-toggle"
      >
        <History className="h-3.5 w-3.5" /> Previous Versions
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 max-h-40 overflow-y-auto">
          {revs === null && <p className="text-xs text-slate-500">Loading…</p>}
          {revs !== null && revs.length === 0 && <p className="text-xs text-slate-500">No previous versions yet. A version is kept every time this page's content is saved.</p>}
          {(revs || []).map((rev) => (
            <div key={rev.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="edit-revision-row">
              <div className="min-w-0">
                <p className="text-xs font-bold text-[#1a2744]">{fmtRevTime(rev.replaced_at)}</p>
                <p className="text-[11px] text-slate-500 truncate">{rev.saved_by ? `Saved by ${rev.saved_by}` : 'Approved default wording'}</p>
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs font-bold btn-press shrink-0" disabled={busy === rev.id} onClick={() => restore(rev)} data-testid="edit-revision-restore">
                {busy === rev.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />} Restore
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// HERO editor (EDIT HERO): text, image upload, ALT, focal, overlay, buttons,
// show toggle, live PREVIEW before save.
// ---------------------------------------------------------------------------
const FOCAL_OPTIONS = [
  { value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' },
  { value: 'left-top', label: 'Left top' }, { value: 'center-top', label: 'Centre top' }, { value: 'right-top', label: 'Right top' },
];
const STYLE_OPTIONS = [
  { value: 'navy', label: 'Navy (standard)' }, { value: 'red', label: 'Red (take action)' }, { value: 'black', label: 'Black (impact)' },
];

const heroForm = (o) => ({
  kicker: o?.kicker || '', title: o?.title || '', intro: o?.intro || '',
  image_url: o?.image_url || '', image_alt: o?.image_alt || '',
  focal: o?.focal || 'right', overlay: o?.overlay ?? 85,
  primary_label: o?.primary_label || '', primary_url: o?.primary_url || '', primary_style: o?.primary_style || 'navy',
  secondary_label: o?.secondary_label || '', secondary_url: o?.secondary_url || '',
  show: o?.show !== false,
});

export const HeroEditorDialog = ({ fullKey, effective, open, onClose }) => {
  const [groupKey, sectionKey] = splitKey(fullKey);
  const { setDraft, clearDraft } = useEditMode();
  const [form, setForm] = useState(() => heroForm(effective));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const fileRef = useRef(null);
  const isHero = !!effective?.hero;

  useEffect(() => {
    if (open) { setForm(heroForm(effective)); setDirty(false); setPreviewing(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useUnsavedWarning(open && dirty);

  const set = (field, value) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (previewing) setDraft(fullKey, next);
      return next;
    });
    setDirty(true);
  };

  const preview = () => {
    const on = !previewing;
    setPreviewing(on);
    if (on) { setDraft(fullKey, form); toast.info('Previewing your changes on the page behind this window. Nothing is saved yet.'); }
    else clearDraft(fullKey);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'page-heroes');
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('image_url', r.data.url);
      toast.success('Image uploaded. Click Save to publish it.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); }
  };

  const close = (force = false) => {
    if (!force && dirty && !confirmDiscard()) return;
    clearDraft(fullKey);
    setPreviewing(false);
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveSection(groupKey, sectionKey, form);
      clearDraft(fullKey);
      setDirty(false);
      toast.success('Saved. The page is updated.');
      close(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="onpage-hero-editor">
        <DialogHeader>
          <DialogTitle>{isHero ? 'Edit Hero' : 'Edit Header'}</DialogTitle>
          <DialogDescription>Changes publish only when you click Save. Use Preview to see them on the page first.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="hero-kicker">Small top label (kicker)</Label>
            <Input id="hero-kicker" value={form.kicker} onChange={(e) => set('kicker', e.target.value)} className="mt-1" data-testid="onpage-hero-kicker" />
          </div>
          <div>
            <Label htmlFor="hero-title">{isHero ? 'Hero H1 (press Enter for a hard line break)' : 'Title'}</Label>
            <Textarea id="hero-title" rows={2} value={form.title} onChange={(e) => set('title', e.target.value)} className="mt-1 font-bold" data-testid="onpage-hero-title" />
          </div>
          <div>
            <Label htmlFor="hero-intro">Supporting text</Label>
            <Textarea id="hero-intro" rows={2} value={form.intro} onChange={(e) => set('intro', e.target.value)} className="mt-1" data-testid="onpage-hero-intro" />
          </div>

          {isHero && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 space-y-3">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><ImageIcon className="h-3.5 w-3.5" /> Hero image</p>
              <div className="flex items-center gap-3">
                {form.image_url ? (
                  <img src={form.image_url.startsWith('http') || form.image_url.startsWith('/branding/') ? form.image_url : fileUrl(form.image_url)} alt="Hero preview" className="h-16 w-28 object-cover rounded-lg border border-slate-200" data-testid="onpage-hero-image-preview" />
                ) : (
                  <div className="h-16 w-28 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-400">No image</div>
                )}
                <div className="space-y-1.5">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="onpage-hero-image-input" />
                  <Button type="button" variant="outline" size="sm" className="h-9 font-bold btn-press" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="onpage-hero-image-upload">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} {form.image_url ? 'Change image' : 'Upload image'}
                  </Button>
                  {form.image_url && (
                    <Button type="button" variant="outline" size="sm" className="h-9 text-xs text-red-600 border-red-200 hover:bg-red-50 btn-press block" onClick={() => set('image_url', '')} data-testid="onpage-hero-image-remove">Remove image</Button>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="hero-alt">Image ALT text (describes the photo)</Label>
                <Input id="hero-alt" value={form.image_alt} onChange={(e) => set('image_alt', e.target.value)} className="mt-1 bg-white" data-testid="onpage-hero-alt" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Photo focal point</Label>
                  <Select value={form.focal} onValueChange={(v) => set('focal', v)}>
                    <SelectTrigger className="h-10 bg-white mt-1" data-testid="onpage-hero-focal"><SelectValue /></SelectTrigger>
                    <SelectContent>{FOCAL_OPTIONS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>White overlay: {form.overlay}%</Label>
                  <Slider value={[form.overlay]} min={40} max={100} step={1} onValueChange={([v]) => set('overlay', v)} className="py-4" data-testid="onpage-hero-overlay" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="hero-p-label">Primary button text</Label>
                  <Input id="hero-p-label" value={form.primary_label} onChange={(e) => set('primary_label', e.target.value)} className="mt-1 bg-white" data-testid="onpage-hero-primary-label" />
                </div>
                <div>
                  <Label htmlFor="hero-p-url">Primary button link</Label>
                  <Input id="hero-p-url" value={form.primary_url} onChange={(e) => set('primary_url', e.target.value)} placeholder="/issue-tracker/report" className="mt-1 bg-white" data-testid="onpage-hero-primary-url" />
                </div>
              </div>
              <div>
                <Label>Primary button style</Label>
                <Select value={form.primary_style} onValueChange={(v) => set('primary_style', v)}>
                  <SelectTrigger className="h-10 bg-white mt-1" data-testid="onpage-hero-primary-style"><SelectValue /></SelectTrigger>
                  <SelectContent>{STYLE_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="hero-s-label">Second button text</Label>
                  <Input id="hero-s-label" value={form.secondary_label} onChange={(e) => set('secondary_label', e.target.value)} className="mt-1 bg-white" data-testid="onpage-hero-secondary-label" />
                </div>
                <div>
                  <Label htmlFor="hero-s-url">Second button link</Label>
                  <Input id="hero-s-url" value={form.secondary_url} onChange={(e) => set('secondary_url', e.target.value)} className="mt-1 bg-white" data-testid="onpage-hero-secondary-url" />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3.5 py-2.5">
                <p className="text-sm font-bold text-[#1a2744]">Show this hero</p>
                <Switch checked={form.show} onCheckedChange={(v) => set('show', v)} data-testid="onpage-hero-show" />
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            {isHero ? (
              <Button type="button" variant="outline" className={`h-11 font-bold btn-press ${previewing ? 'border-[#b91c1c] text-[#b91c1c]' : ''}`} onClick={preview} data-testid="onpage-hero-preview">
                <Eye className="h-4 w-4 mr-1.5" /> {previewing ? 'Previewing… (click to stop)' : 'Preview'}
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="h-11 font-bold btn-press" onClick={() => close()} data-testid="onpage-hero-cancel">Cancel</Button>
              <Button type="button" className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" onClick={save} disabled={saving} data-testid="onpage-hero-save">
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
              </Button>
            </div>
          </div>
          <RevisionsPanel groupKey={groupKey} onRestored={() => close(true)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// RICH TEXT editor (EDIT THIS SECTION): the same WYSIWYG used in Admin ->
// Page Content (bold / italic / underline / links / H2 / H3 / lists), saved
// through the same sanitized content records.
// ---------------------------------------------------------------------------
export const RichEditorDialog = ({ richKey, currentHtml, label, open, onClose }) => {
  const [groupKey, sectionKey] = splitKey(richKey);
  const [html, setHtml] = useState(currentHtml || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setHtml(currentHtml || ''); setDirty(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useUnsavedWarning(open && dirty);

  const close = (force = false) => {
    if (!force && dirty && !confirmDiscard()) return;
    onClose();
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveSection(groupKey, sectionKey, { html });
      setDirty(false);
      toast.success('Saved. The page is updated.');
      close(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="onpage-rich-editor">
        <DialogHeader>
          <DialogTitle>Edit This Section{label ? `: ${label}` : ''}</DialogTitle>
          <DialogDescription>Leave the editor empty and Save to bring back the approved built-in wording. Nothing publishes until you click Save.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RichTextEditor value={html} onChange={(v) => { setHtml(v); setDirty(true); }} placeholder="Leave empty to keep the built-in wording…" />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="h-11 font-bold btn-press" onClick={() => close()} data-testid="onpage-rich-cancel">Cancel</Button>
            <Button type="button" className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" onClick={save} disabled={saving} data-testid="onpage-rich-save">
              {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save
            </Button>
          </div>
          <RevisionsPanel groupKey={groupKey} onRestored={() => close(true)} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Convenience wrappers used inside Shared.jsx --------------------------------*/

// EDIT HERO / EDIT HEADER control for PageHeader + SectionHeader
export const HeaderEditControl = ({ fullKey, effective, label = 'Edit Hero', testId }) => {
  const { isAdmin, editMode } = useEditMode();
  const [open, setOpen] = useState(false);
  if (!isAdmin || !editMode || !fullKey || !effective) return null;
  return (
    <>
      <EditBadge label={label} onClick={() => setOpen(true)} testId={testId || `edit-hero-${fullKey}`} />
      {open && <HeroEditorDialog fullKey={fullKey} effective={effective} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

// EDIT THIS SECTION control for RichSection
export const RichEditControl = ({ richKey, currentHtml, label }) => {
  const { isAdmin, editMode } = useEditMode();
  const [open, setOpen] = useState(false);
  if (!isAdmin || !editMode || !richKey) return null;
  return (
    <>
      <EditBadge label="Edit This Section" onClick={() => setOpen(true)} testId={`edit-section-${richKey}`} />
      {open && <RichEditorDialog richKey={richKey} currentHtml={currentHtml} label={label} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};
